import { Text, Flex } from "@mantine/core";
import { Modal } from "../../../../components";
import {
  IMyPlans,
  EProductType,
} from "../../../../interfaces/MyPlans/IMyPlans";
import { client_selected } from "../../../../client";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  postAcceptanceTerms,
  postCancelPlan,
  postConfigurationPlan,
} from "../../../../hooks/useMyPlans";
import { useNotificationControl } from "../../../../context/notificationContext";
import { useMemo, useState, useEffect } from "react";
import { addDaysToDate } from "../../../../utils/date";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { IProductOrganization } from "../../../../interfaces/Products/IProductDetail";

interface IProps {
  product: IMyPlans;
  isRefetching: boolean;
  children: React.ReactNode;
}

interface IFormParameters {
  broker_integration_id?: number | string;
  qty: number | undefined;
  is_inverse_side: boolean | null;
  is_algorithm_running: boolean | null;
  is_machine_running: boolean | undefined;
  value: number | undefined;
  brokerSelected?: number | string;
  is_accepted_term: boolean | undefined;
  confirm_that_you_read_1?: boolean | undefined;
  confirm_that_you_read_2?: boolean | undefined;
  confirm_that_you_read_3?: boolean | undefined;
}

interface ITextAndDisable {
  label: string;
  tooltip: string | React.ReactNode;
  disable: boolean;
  max: number | undefined;
}

interface SelectControlledProps {
  value: string;
  label: string;
  disabled?: boolean;
}
// Defina a interface do contexto
interface IConfigContext {
  form: UseFormReturn<IFormParameters, any>;
  isRefetching: boolean;
  product: IMyPlans;
  modals: {
    setIsOpenInfoBitget: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenActiveAlgorithm: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenModalQuarantine: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenModalOkxCredentials: React.Dispatch<React.SetStateAction<boolean>>;
    openModalOkxCredentials: boolean;
  };
  handlers: {
    handleUpdateAlgorithm: () => void;
    handleCancel: () => void;
  };
  states: {
    isAcceptedTerm: boolean;
    isAlgorithmRunning: boolean;
    isPendingScheduledActions: boolean;
    isEnabledToEdit: boolean;
    isEnabledToRunAlgorithm: boolean;
    hasCancelButton: boolean;
    isDirty: boolean;
    disabledEdit: boolean;
    setDisabledEdit: React.Dispatch<React.SetStateAction<boolean>>;
    disableNumber: ITextAndDisable;
    brokersAccountAvailable: Array<SelectControlledProps>;
    valueSelect: {
      min_value_config: number;
      max_value_config: number;
      multiply_value_config: number;
      has_value_config: boolean;
    };
    hasMoreThanOneOrganization: boolean;
    brokerSelected: IProductOrganization | undefined;
    brokersAvailable: Array<SelectControlledProps>;
    shouldUseOkxCredentialsModal: boolean;
  };
}

// Criação do contexto customizado
export const ConfigContext = createContext<IConfigContext | undefined>(
  undefined
);
export function useConfigContext() {
  const ctx = useContext(ConfigContext);
  if (!ctx)
    throw new Error("useConfigContext must be used within ConfigProvider");
  return ctx;
}

export default function ConfigProvider({
  product,
  isRefetching,
  children,
}: IProps) {
  const { notificationControl } = useNotificationControl();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const hasCancelButton = client_selected.hasCancelButton;
  const [isOpenInfoBitget, setIsOpenInfoBitget] = useState(false);
  const [openActiveAlgorithm, setOpenActiveAlgorithm] = useState(false);
  const [openModalQuarantine, setOpenModalQuarantine] = useState(false);
  const [isSuccessAlgorithm, setIsSuccessAlgorithm] = useState(false);
  const [openModalErrorBitget, setOpenModalErrorBitget] =
    useState<boolean>(false);
  const [disabledEdit, setDisabledEdit] = useState<boolean>(true);
  const [openModalOkxCredentials, setOpenModalOkxCredentials] = useState(false);
  const organizations = product.product.organizations;
  const hasMoreThanOneOrganization = organizations.length > 1;

  // Check if product should use OKX credentials modal instead of OAuth
  const okxProductCredentialsEnv = process.env.REACT_APP_OKX_PRODUCT_CREDENTIALS;
  const okxProductCredentialsIds = okxProductCredentialsEnv
    ? okxProductCredentialsEnv.split(",").map((id) => id.trim()).filter(Boolean)
    : [];
  const shouldUseOkxCredentialsModal = okxProductCredentialsIds.length > 0 && okxProductCredentialsIds.includes(product?.product?.id?.toString());

  const dateFinishScheduled = addDaysToDate(
    product?.last_change_config || new Date(),
    product?.product?.configuration_changes_quarantine_in_days
  );

  const isAcceptedTerm = !!product?.is_accepted_term;
  const isAlgorithmRunning = !!product?.is_algorithm_running;
  const isPendingScheduledActions = !!product?.has_pending_scheduled_actions;

  const isEnabledToEdit = isAcceptedTerm && !disabledEdit;

  const methods = useForm<IFormParameters, any>({
    mode: "all",
    defaultValues: {
      broker_integration_id: !!product?.broker_integration_id
        ? product.broker_integration_id.toString()
        : undefined,
      qty: !!product?.qty ? product.qty : 1,
      is_inverse_side: product?.is_inverse_side,
      is_algorithm_running: isAlgorithmRunning,
      is_accepted_term: !!product?.is_accepted_term,
      value:
        !!product?.value && !!product?.product?.has_value_config
          ? product?.value
          : product?.product?.min_value_config || 0,
    },
  });

  const {
    watch,
    handleSubmit,
    setValue,
    formState: { isDirty },
  } = methods;

  const watchedBrokerIntegrationId = watch("broker_integration_id");

  const brokerAccountSelected = product.brokers_available.find(
    (broker) => broker.id.toString() === watchedBrokerIntegrationId
  );

  useEffect(() => {
    if (watchedBrokerIntegrationId && product?.brokers_available) {
      if (brokerAccountSelected && !brokerAccountSelected.is_allowed) {
        setOpenModalErrorBitget(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedBrokerIntegrationId, product?.brokers_available, setValue]);

  const brokerSelected = organizations.find(
    (organization) => organization.id.toString() === watch("brokerSelected")
  );
  useEffect(() => {
    if (hasMoreThanOneOrganization) {
      setValue("brokerSelected", undefined);
    } else {
      setValue("brokerSelected", organizations[0].id.toString());
    }
  }, [hasMoreThanOneOrganization]);

  const watchedValues = watch();

  const hasInitialValues =
    useMemo(
      () => Object.values(watchedValues).some((value) => !!value),
      [watchedValues]
    ) && !!watch().broker_integration_id;

  const isEnabledToRunAlgorithm = isAlgorithmRunning
    ? isPendingScheduledActions
    : !isEnabledToEdit || isPendingScheduledActions || !hasInitialValues;

  const { mutateAsync: acceptTermsAsync } = useMutation(postAcceptanceTerms, {
    onError: (error) => {
      // console.log(error);
      notificationControl({
        message: t("meu_portfolio.minhas_compras.card_product.erro_terms"),
        type: "danger",
      });
    },
  });

  const { mutateAsync: activateAlgorithmAsync } = useMutation(
    postConfigurationPlan,
    {
      onSuccess: () => {
        setOpenActiveAlgorithm(false);
        setIsSuccessAlgorithm(true);
        setDisabledEdit(false);
      },
      onError: (error: any) => {
        // console.log(error);
        if (error.response.status === 400) {
          notificationControl({
            message: !!error?.response?.data?.message
              ? error?.response?.data?.message
              : isAlgorithmRunning
              ? t(
                  "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_acctive_algorithm.is_algorithm_running.erro_400"
                )
              : t(
                  "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_acctive_algorithm.is_algorithm_not_running.erro_400"
                ),
            type: "danger",
          });
        } else if (error.response.status === 409) {
          notificationControl({
            message: isAlgorithmRunning
              ? t(
                  "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_acctive_algorithm.is_algorithm_running.erro_409"
                )
              : t(
                  "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_acctive_algorithm.is_algorithm_not_running.erro_409"
                ),
            type: "danger",
          });
        } else {
          notificationControl({
            message: isAlgorithmRunning
              ? t(
                  "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_acctive_algorithm.is_algorithm_running.erro"
                )
              : t(
                  "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_acctive_algorithm.is_algorithm_not_running.erro"
                ),
            type: "danger",
          });
        }
        setOpenActiveAlgorithm(false);
      },
    }
  );

  const { mutate: mutateUpdateAlgorithm } = useMutation(postConfigurationPlan, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["MyPlans"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["MyHistoryPlans"] });
      notificationControl({
        message: t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_update_algorithm.sucess"
        ),
        type: "success",
      });
      setOpenModalQuarantine(false);
      setDisabledEdit(true);
    },
    onError: (error: any) => {
      // console.log("🚀 ~ error:", error);
      setOpenModalQuarantine(false);
      if (error.response.status === 400) {
        notificationControl({
          message: !!error?.response?.data?.message
            ? error?.response?.data?.message
            : t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_update_algorithm.erro_400"
              ),
          type: "danger",
        });
      } else if (error.response.status === 409) {
        notificationControl({
          message: t(
            "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_update_algorithm.erro_409"
          ),
          type: "danger",
        });
      } else {
        notificationControl({
          message: t(
            "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_update_algorithm.erro"
          ),
          type: "danger",
        });
      }
    },
  });

  const { mutate } = useMutation(postCancelPlan, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["MyHistoryPlans", product?.product?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["MyPlans"], exact: false });
      setDisabledEdit(true);
    },
    onError: (error) => {
      notificationControl({
        title: t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_cancel.title"
        ),
        message: t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.mutate_cancel.message"
        ),
        type: "danger",
      });
    },
  });

  const handleUpdateAlgorithm = () => {
    const dataUpdateAlgorithm: IFormParameters = {
      broker_integration_id: Number(watch("broker_integration_id")),
      qty: watch("qty"),
      is_inverse_side: watch("is_inverse_side"),
      is_algorithm_running: true,
      is_machine_running: true,
      value: watch("value"),
      is_accepted_term: watch("is_accepted_term"),
    };
    mutateUpdateAlgorithm({ id: product?.id, configs: dataUpdateAlgorithm });
  };

  const onSubmit = async (data: IFormParameters) => {
    const newData: IFormParameters = {
      broker_integration_id: !!data?.broker_integration_id
        ? Number(data?.broker_integration_id)
        : undefined,
      qty: data?.qty,
      is_inverse_side: data?.is_inverse_side,
      is_algorithm_running: !isAlgorithmRunning,
      is_machine_running: true,
      value: data?.value,
      is_accepted_term: data?.is_accepted_term,
    };

    const desactivateAlgorithm: IFormParameters = {
      is_algorithm_running: !isAlgorithmRunning,
      broker_integration_id: undefined,
      is_machine_running: true,
      qty: undefined,
      is_inverse_side: null,
      value: undefined,
      is_accepted_term: data?.is_accepted_term,
    };
    try {
      if (data?.is_accepted_term) {
        // console.log(
        //   "🚀 ~ !!!data?.is_accepted_term:",
        //   !!!data?.is_accepted_term
        // );
        await acceptTermsAsync(product.id);
        newData.is_accepted_term = true;
        desactivateAlgorithm.is_accepted_term = true;
      }

      await activateAlgorithmAsync({
        id: product?.id,
        configs: !isAlgorithmRunning ? newData : desactivateAlgorithm,
      });
    } catch (error) {
      // Erros já são tratados nos onError das mutations
    }
  };

  const handleCancel = () => {
    mutate(product?.id);
  };

  const handleClose = () => {
    queryClient.invalidateQueries({ queryKey: ["MyPlans"], exact: false });
    queryClient.invalidateQueries({ queryKey: ["MyHistoryPlans"] });
    setOpenActiveAlgorithm(false);
    setIsSuccessAlgorithm(false);
    setDisabledEdit(true);
  };

  const handleCloseModalErrorBitget = () => {
    setValue("broker_integration_id", "");

    setOpenModalErrorBitget(false);
  };

  const disableNumber = (): ITextAndDisable => {
    const productType = product?.product?.type;
    const qtyContract = product?.plan?.qty_contract;
    const qtyContractIsOne = qtyContract === 1;

    let textAndDisable: ITextAndDisable = {
      label: t(
        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.label"
      ),
      tooltip: qtyContractIsOne
        ? t(
            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.analyst_qty"
          )
        : t(
            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.tooltip"
          ),
      disable: qtyContractIsOne,
      max: !!qtyContract ? qtyContract : undefined,
    };

    if (productType === EProductType.AnalystQty) {
      textAndDisable = {
        ...textAndDisable,
        label: t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.leverage"
        ),
        tooltip: t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.analyst_qty"
        ),
        disable: true,
      };
    }

    if (productType === EProductType.FixedQty) {
      textAndDisable = {
        ...textAndDisable,
        label: t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.leverage"
        ),
        tooltip: qtyContractIsOne
          ? t(
              "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.analyst_qty"
            )
          : t(
              "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.fixed_qty"
            ),
      };
    }

    if (productType === EProductType.MultipleQty) {
      textAndDisable = {
        ...textAndDisable,
        label: t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.leverage"
        ),
        tooltip: qtyContractIsOne ? (
          t(
            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.analyst_qty"
          )
        ) : (
          <Flex maw="340px" w={"340px"}>
            <Text
              style={{
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                width: "340px",
              }}
            >
              {t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.multiple_qty_1"
              )}
              <br />
              {t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.multiple_qty_2"
              )}
            </Text>
          </Flex>
        ),
      };
    }

    if (productType === EProductType.SubAccountsQty) {
      textAndDisable = {
        ...textAndDisable,
        label: t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.sub_accounts_qty_label"
        ),
        tooltip: qtyContractIsOne
          ? t(
              "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.analyst_qty"
            )
          : t(
              "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.disable_number.sub_accounts_qty_tooltip"
            ),
      };
    }

    return textAndDisable;
  };

  const brokersAccountAvailable = useMemo(() => {
    return !!product?.brokers_available && product.brokers_available.length > 0
      ? product.brokers_available.map(
          (broker: IMyPlans["brokers_available"][0]) => {
            return {
              value: broker.id.toString(),
              label: `${broker.name} - ${broker.customer_friendly_account}`,
            };
          }
        )
      : [
          {
            value: "",
            label: t(
              "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.broker_select_label"
            ),
            disabled: true,
          },
        ];
  }, [product?.brokers_available, t]);

  const brokersAvailable = useMemo(() => {
    // console.log(organizations);

    return !!organizations && organizations.length > 0
      ? organizations.map((organization: IProductOrganization) => {
          return {
            value: organization.id.toString(),
            label: organization.name,
          };
        })
      : [
          {
            value: "",
            label: t("Não há nenhuma corretora disponivel para este serviço"),
            disabled: true,
          },
        ];
  }, [organizations, t]);

  const valueSelect = useMemo(
    () => ({
      min_value_config: product?.product?.min_value_config || 500,
      max_value_config: product?.product?.max_value_config || 5000,
      multiply_value_config: product?.product?.multiply_value_config || 500,
      has_value_config: product?.product?.has_value_config || false,
    }),
    [product?.product]
  );

  const disableNumberResult = useMemo(() => disableNumber(), [disableNumber]);

  // Exponha só o necessário!
  const contextValue: IConfigContext = {
    form: methods,
    isRefetching,
    product,
    modals: {
      setIsOpenInfoBitget,
      setOpenActiveAlgorithm,
      setOpenModalQuarantine,
      setOpenModalOkxCredentials,
      openModalOkxCredentials,
    },
    handlers: {
      handleUpdateAlgorithm, //
      handleCancel, //
    },
    states: {
      isAcceptedTerm,
      isAlgorithmRunning,
      isPendingScheduledActions,
      isEnabledToEdit,
      isEnabledToRunAlgorithm,
      hasCancelButton,
      isDirty,
      disabledEdit,
      setDisabledEdit,
      disableNumber: disableNumberResult,
      brokersAccountAvailable,
      valueSelect,
      hasMoreThanOneOrganization,
      brokersAvailable,
      brokerSelected,
      shouldUseOkxCredentialsModal,
    },
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
          {children}
        </form>
      </FormProvider>
      {/* ...modais globais, se necessário */}
      {/* openModalErrorBitget */}
      <Modal
        type={"warning"}
        textModal={t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_error_bitget.text_modal"
        )}
        titleSeccondButton={t("global.modal.close")}
        // contentEspecial={
        //   <Text
        //     c="basic.4"
        //     fz="14px"
        //     lh="155%"
        //     ta={"center"}
        //     w="320px"
        //     mb="32px"
        //   >
        //     {t("meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_error_bitget.content_especial")}
        //     <Link
        //       to={"/marketplace"}
        //       target="_blank"
        //       style={{ color: theme.colors.basic[5], fontWeight: 700 }}
        //     >
        //       {t("meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_error_bitget.click")}
        //     </Link>
        //   </Text>
        // }
        description={t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_error_bitget.description",
          {
            name: brokerAccountSelected?.name,
            customer_friendly_account:
              brokerAccountSelected?.customer_friendly_account,
          }
        )}
        open={openModalErrorBitget}
        setClose={handleCloseModalErrorBitget}
        secondClick={handleCloseModalErrorBitget}
      />

      {/* openModalQuarantine  */}
      <Modal
        type={"warning"}
        textModal={t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_quarantine.text_modal"
        )}
        titleFirstButton={t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_quarantine.title_first_button",
          { dateFinishScheduled: dateFinishScheduled }
        )}
        titleSeccondButton={t("global.modal.close")}
        description={t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_quarantine.description",
          { days: product?.product?.configuration_changes_quarantine_in_days }
        )}
        contentEspecial={
          <Text
            c="basic.4"
            fz="14px"
            lh="155%"
            ta={"center"}
            w="320px"
            mb="16px"
          >
            {t(
              "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_quarantine.content_especial"
            )}
          </Text>
        }
        open={openModalQuarantine}
        setClose={() => setOpenModalQuarantine(false)}
        secondClick={() => setOpenModalQuarantine(false)}
        firstClick={handleUpdateAlgorithm}
      />
      {/* openActiveAlgorithm  */}
      <Modal
        type={"warning"}
        textModal={
          isAlgorithmRunning
            ? t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_active_algorithm.is_algorithm_running.text_modal"
              )
            : t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_active_algorithm.is_algorithm_not_running.text_modal"
              )
        }
        titleFirstButton={
          isAlgorithmRunning
            ? t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_active_algorithm.is_algorithm_running.title_first_button"
              )
            : t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_active_algorithm.is_algorithm_not_running.title_first_button"
              )
        }
        titleSeccondButton={t("global.modal.close")}
        description={
          isAlgorithmRunning
            ? t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_active_algorithm.is_algorithm_running.description"
              )
            : t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_active_algorithm.is_algorithm_not_running.description"
              )
        }
        contentEspecial={
          <Text
            c="basic.4"
            fz="14px"
            lh="155%"
            ta={"center"}
            w="320px"
            mb="16px"
          >
            {t(
              "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_active_algorithm.content_especial"
            )}
          </Text>
        }
        open={openActiveAlgorithm}
        setClose={() => setOpenActiveAlgorithm(false)}
        secondClick={() => setOpenActiveAlgorithm(false)}
        firstClick={handleSubmit(onSubmit)}
      />
      {/* isSuccessAlgorithm  */}
      <Modal
        type={"success"}
        titleSeccondButton={t("global.modal.close")}
        description={
          isAlgorithmRunning
            ? t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_success_algorithm.is_algorithm_running.description"
              )
            : t(
                "meu_portfolio.minhas_compras.configuracao_do_serviço.config_provider.modal_success_algorithm.is_algorithm_not_running.description"
              )
        }
        open={isSuccessAlgorithm}
        setClose={handleClose}
        secondClick={handleClose}
      />
    </ConfigContext.Provider>
  );
}
